<template>
  <div ref="root" class="ops-clip" data-test="ops-wall-clip">
    <div ref="box" class="ops-clip__items">
      <div
        v-for="(item, index) in items"
        :key="keyOf(item, index)"
        class="ops-clip__item"
        :class="{ 'ops-clip__item--off': isOff(index) }"
        :aria-hidden="isOff(index) ? 'true' : null"
        data-test="ops-wall-clip-item"
      >
        <slot :item="item" :index="index"></slot>
      </div>
    </div>
    <div v-if="hiddenCount > 0" class="ops-clip__more" data-test="ops-wall-more">+{{ hiddenCount }} more</div>
  </div>
</template>

<script>
/**
 * One column of the Wall that NEVER grows the page: it fills the height its parent gives it, draws as many items as fit, and says
 * "+N more" for the rest (the TV has no scroll bar and nobody to press one).
 *
 * HOW. Every item is always in the DOM, in one natural-height box inside an `overflow: hidden` root. After each render (and when the
 * root is resized, e.g. the Wall scaled to another screen) `fit()` measures the items: the ones that do not fit above the "+N more" line
 * get `visibility: hidden`, so no half card is ever drawn. The measuring never changes the layout (hidden items still take their
 * space), so it cannot oscillate; the "+N more" line is absolutely positioned over the bottom of the root.
 *
 * At least ONE item is always drawn (clipped by the root if the column is that short) — a column that says only "+3 more" is useless;
 * a single item is simply clipped.
 * With no layout at all (a test DOM, a hidden tab: clientHeight 0) nothing is hidden.
 *
 *   props   items  the list · itemKey  (item, index) → key
 *   slot    default, scoped: { item, index }
 */
const MORE_LINE_PX = 40;

export default {
  name: 'OpsWallClip',

  props: {
    items: { type: Array, default: () => [] },
    itemKey: { type: Function, default: null },
  },

  data() {
    return { limit: null };
  },

  computed: {
    hiddenCount() {
      return this.limit === null ? 0 : Math.max(0, this.items.length - this.limit);
    },
  },

  watch: {
    items() {
      this.$nextTick(this.fit);
    },
  },

  mounted() {
    this.observer = null;
    if (typeof ResizeObserver === 'function') {
      this.observer = new ResizeObserver(() => { this.fit(); });
      this.observer.observe(this.$refs.root);
      this.observer.observe(this.$refs.box);
    }
    this.fit();
  },

  updated() {
    this.fit();
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
  },

  methods: {
    keyOf(item, index) {
      const key = this.itemKey ? this.itemKey(item, index) : (item && item.key);
      return key === undefined || key === null ? index : key;
    },

    isOff(index) {
      return this.limit !== null && index >= this.limit;
    },

    /** How many items fit in the root's height — null when they all do (or nothing is laid out). */
    fit() {
      const root = this.$refs.root;
      const box = this.$refs.box;
      if (!root || !box) return;
      const available = root.clientHeight;
      const kids = box.children;
      if (!available || kids.length < 2 || box.offsetHeight <= available + 1) {
        if (this.limit !== null) this.limit = null;
        return;
      }
      const room = available - MORE_LINE_PX;
      let fits = 0;
      for (let i = 0; i < kids.length; i += 1) {
        if (kids[i].offsetTop + kids[i].offsetHeight <= room) fits += 1;
        else break;
      }
      const next = Math.min(kids.length - 1, Math.max(1, fits));
      if (next !== this.limit) this.limit = next;
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-clip {
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

// offsetTop of an item is measured from here
.ops-clip__items {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ops-clip__item { min-width: 0; }
.ops-clip__item--off { visibility: hidden; }

.ops-clip__more {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--w-chip);
  color: var(--w-sub);
  font-size: 20px;
  font-weight: 800;
}
</style>
